import { useAuthQuery } from '@/api/auth-query';
import { FieldInfo } from '@/components/FieldInfo';
import { studyQueryKeys, userQueryKeys } from '@/config/queryKeys';
import { smIconSize } from '@/constants';
import { apiClient } from '@/lib/api-client';
import { extractFieldErrors } from '@/lib/api-errors';
import { remapServerFieldErrors } from '@/lib/remap-server-field-errors';
import type { StudyCreate, StudyCreateResponse } from '@/types';
import { AutoComplete } from '@nautilusbio/nb-design-system';
import Accordion from '@nautilusbio/nb-design-system/Accordion';
import { Button } from '@nautilusbio/nb-design-system/Button';
import type { TextSizes } from '@nautilusbio/nb-design-system/helpers';
import { Input, InputProps } from '@nautilusbio/nb-design-system/Input';
import { getStackClassName, Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Contact2, Loader, User, X } from 'lucide-react';
import React, { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import createStyles from '../../../components/Create/styles.module.css';

import { isContactFilled, nameSchema, splitFullName } from './create-form-utils';

const inputSharedProps: Partial<InputProps> = {
  autoComplete: 'off',
  className: createStyles.uiInput,
  size: 'xs',
};

const CreateForm: FC<{ onSuccess: () => void; size?: TextSizes; userOrg: string }> = ({
  onSuccess,
  size = 'xl',
  userOrg,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedUsers, setSelectedUsers] = useState<Array<{ id: string; full_name: string }>>([]);
  const [userSearchValue, setUserSearchValue] = useState('');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const { data: currentUser } = useAuthQuery();

  const { data: orgUsers = [] } = useQuery({
    queryKey: userQueryKeys.orgUsers(userOrg),
    queryFn: async () => {
      const result = await apiClient.get<Array<{ id: string; full_name: string; email: string }>>(
        userQueryKeys.endpoints.orgUsers(userOrg),
      );
      return result ?? [];
    },
    staleTime: 60_000,
  });

  const userOptions = useMemo(
    () =>
      orgUsers
        .filter(u => !selectedUsers.some(s => s.id === u.id))
        .map(u => ({ id: u.id, label: u.full_name, value: u.id })),
    [orgUsers, selectedUsers],
  );

  const addStudy = useMutation({
    mutationFn: async (newStudy: StudyCreate): Promise<StudyCreateResponse> => {
      const response = await apiClient.post<StudyCreateResponse>(studyQueryKeys.endpoints.list(userOrg), newStudy);

      if (!response) {
        throw new Error('Study creation failed - no response from server');
      }

      return response;
    },
    onError: error => {
      const fieldErrors = extractFieldErrors(error);
      // Prefer the pure helper to keep logic single-sourced and testable
      const remappedErrors = remapServerFieldErrors(fieldErrors);
      form.setErrorMap(remappedErrors);
    },
    scope: {
      id: 'studies',
    },
  });

  const form = useForm({
    defaultValues: {
      description: '',
      name: '',
      contact: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
      },
    },
    onSubmit: async ({ value }) => {
      // Clear previous server errors before submit using the public form API
      form.setErrorMap({});

      const payload: StudyCreate = {
        name: value.name,
        description: value.description.trim() || null,
        allowed_user_ids: selectedUsers.map(u => u.id),
        ...(isContactFilled(value.contact) && {
          contact: {
            first_name: value.contact.first_name,
            last_name: value.contact.last_name,
            email: value.contact.email,
            phone: value.contact.phone.trim() || null,
          },
        }),
      };

      const studyPromise = addStudy.mutateAsync(payload);

      // Show loading/success/error toast purely for UI feedback
      toast.promise(studyPromise, {
        loading: t('study.creating', { ns: 'create' }),
        success: () => `${value.name} ${t('study.success', { ns: 'create' })}`,
        error: () => t('study.failed', { ns: 'create' }),
      });

      // Await the mutation so side effects only run after success.
      const result = await studyPromise;

      // Success: reset form, invalidate caches (via onSuccess), navigate
      form.reset();
      setSelectedUsers([]);
      setUserSearchValue('');
      setIsContactOpen(false);
      onSuccess();
      navigate({ params: { studyId: result.id }, to: '/studies/$studyId' });
    },
  });

  function fillContact(first: string, last: string, email: string) {
    form.setFieldValue('contact.first_name', first);
    form.setFieldValue('contact.last_name', last);
    form.setFieldValue('contact.email', email);
    setIsContactOpen(true);
  }

  return (
    <div className={createStyles.formContainer}>
      <form
        className={createStyles.form}
        onSubmit={async e => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <form.Field
          name="name"
          validators={{
            onChange: nameSchema,
            onMount: nameSchema,
          }}
        >
          {field => {
            const label = {
              children: t('general:name'),
              htmlFor: field.name,
              id: `label-${field.name}`,
            };

            return (
              <>
                <Input
                  {...inputSharedProps}
                  // oxlint-disable-next-line no-autofocus
                  autoFocus={true}
                  data-testid={`input-${field.name}`}
                  id={field.name}
                  label={label}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                  value={field.state.value}
                />

                <FieldInfo field={field} />
              </>
            );
          }}
        </form.Field>

        <form.Field name="description">
          {field => {
            const label = {
              children: t('general:desc'),
              htmlFor: field.name,
              id: `label-${field.name}`,
            };

            return (
              <>
                <Textarea
                  autoComplete="off"
                  className={createStyles.uiInput}
                  data-testid={`input-${field.name}`}
                  id={field.name}
                  label={label}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={({ target }: { target: { value: string } }) => field.handleChange(target.value)}
                  rows={5}
                  size="xs"
                  value={field.state.value}
                />

                <FieldInfo field={field} />
              </>
            );
          }}
        </form.Field>

        <AutoComplete
          data-testid="input-allowed-users"
          isClearable={false}
          label={{
            children: t('study.team_members', { ns: 'create' }),
            id: 'label-allowed-users',
          }}
          onInputChange={setUserSearchValue}
          onSelectionChange={key => {
            if (!key) return;
            const user = orgUsers.find(u => u.id === String(key));
            if (!user) return;
            setSelectedUsers(prev => [...prev, { id: user.id, full_name: user.full_name }]);
            setUserSearchValue('');
          }}
          options={userOptions}
          placeholder={t('study.add_member_placeholder', { ns: 'create' })}
          size={inputSharedProps.size}
          value={userSearchValue}
        />

        {selectedUsers.length > 0 && (
          <ul className={createStyles.selectedUsersList}>
            {selectedUsers.map(user => (
              <li
                className={clsx(
                  createStyles.selectedUserRow,
                  getStackClassName(['displayflex', 'aicenter', 'jcspace-between', 'horizontal', 'gap2']),
                )}
                key={user.id}
              >
                <Text as="span">{user.full_name}</Text>

                <Tooltip
                  content={t('study.make_contact_tooltip', { ns: 'create', name: user.full_name })}
                  placement="top"
                  trigger={
                    <Button
                      aria-label={t('study.make_contact_tooltip', { ns: 'create', name: user.full_name })}
                      className={createStyles.selectedUserMakeContact}
                      data-testid={`make-contact-${user.id}`}
                      onPress={() => {
                        const found = orgUsers.find(u => u.id === user.id);
                        const { first, last } = splitFullName(user.full_name);
                        const email = found?.email ?? '';
                        fillContact(first, last, email);
                      }}
                      visual="unstyled"
                    >
                      <Contact2 size={smIconSize} />
                    </Button>
                  }
                />

                <Button
                  aria-label={t('study.remove_user', { ns: 'create', name: user.full_name })}
                  data-testid={`remove-user-${user.id}`}
                  onPress={() => setSelectedUsers(prev => prev.filter(u => u.id !== user.id))}
                  size="sm"
                  visual="unstyled"
                >
                  <X size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <Accordion
          classNames={{
            panel: clsx(getStackClassName(['displayflex', 'vertical', 'gap4']), createStyles.contactAccordionPanel),
          }}
          data-testid="accordion-contact"
          headingLevel={4}
          isExpanded={isContactOpen}
          label={
            <Stack
              alignItems="center"
              className={createStyles.contactAccordionLabel}
              fullWidth
              gap={2}
              justifyContent="space-between"
              orientation="horizontal"
            >
              <Text as="span">{t('study.contact_section', { ns: 'create' })}</Text>

              <Button
                data-testid="use-my-details-btn"
                isDisabled={!currentUser}
                onClick={e => e.stopPropagation()}
                onPress={() => {
                  if (!currentUser) return;
                  const { first, last } = splitFullName(currentUser.full_name);
                  fillContact(first, last, currentUser.email);
                }}
                size="xs"
                visual="outline"
              >
                <User size={smIconSize} /> {t('study.use_my_details', { ns: 'create' })}
              </Button>
            </Stack>
          }
          onExpandedChange={setIsContactOpen}
          showChevron
          unmountOnCollapse
          variant="bordered"
        >
          <form.Field
            name="contact.first_name"
            validators={{
              onChange: ({ value, fieldApi }) => {
                if (!isContactFilled(fieldApi.form.state.values.contact)) return undefined;
                if (!value.trim()) return t('validation_first_name_required', { ns: 'general' });
                return undefined;
              },
              onSubmit: ({ value, fieldApi }) => {
                if (!isContactFilled(fieldApi.form.state.values.contact)) return undefined;
                if (!value.trim()) return t('validation_first_name_required', { ns: 'general' });
                return undefined;
              },
            }}
          >
            {field => {
              const label = {
                children: t('first_name', { ns: 'general' }),
                htmlFor: 'contact-first-name',
                id: 'label-contact-first-name',
              };
              return (
                <>
                  <Input
                    autoComplete="off"
                    className={createStyles.uiInput}
                    data-testid="input-contact-first-name"
                    id="contact-first-name"
                    label={label}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    size={size}
                    value={field.state.value}
                  />
                  <FieldInfo field={field} />
                </>
              );
            }}
          </form.Field>

          <form.Field
            name="contact.last_name"
            validators={{
              onChange: ({ value, fieldApi }) => {
                if (!isContactFilled(fieldApi.form.state.values.contact)) return undefined;
                if (!value.trim()) return t('validation_last_name_required', { ns: 'general' });
                return undefined;
              },
              onSubmit: ({ value, fieldApi }) => {
                if (!isContactFilled(fieldApi.form.state.values.contact)) return undefined;
                if (!value.trim()) return t('validation_last_name_required', { ns: 'general' });
                return undefined;
              },
            }}
          >
            {field => {
              const label = {
                children: t('last_name', { ns: 'general' }),
                htmlFor: 'contact-last-name',
                id: 'label-contact-last-name',
              };

              return (
                <>
                  <Input
                    autoComplete="off"
                    className={createStyles.uiInput}
                    data-testid="input-contact-last-name"
                    id="contact-last-name"
                    label={label}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    size={size}
                    value={field.state.value}
                  />
                  <FieldInfo field={field} />
                </>
              );
            }}
          </form.Field>

          <form.Field
            name="contact.email"
            validators={{
              onChange: ({ value, fieldApi }) => {
                if (!isContactFilled(fieldApi.form.state.values.contact)) return undefined;
                if (!value.trim()) return t('validation_email_required', { ns: 'general' });
                const result = z.string().email().safeParse(value);
                return result.success ? undefined : t('validation_email_invalid', { ns: 'general' });
              },
              onSubmit: ({ value, fieldApi }) => {
                if (!isContactFilled(fieldApi.form.state.values.contact)) return undefined;
                if (!value.trim()) return t('validation_email_required', { ns: 'general' });
                const result = z.string().email().safeParse(value);
                return result.success ? undefined : t('validation_email_invalid', { ns: 'general' });
              },
            }}
          >
            {field => {
              const label = {
                children: t('email', { ns: 'general' }),
                htmlFor: 'contact-email',
                id: 'label-contact-email',
              };
              return (
                <>
                  <Input
                    autoComplete="email"
                    className={createStyles.uiInput}
                    data-testid="input-contact-email"
                    id="contact-email"
                    label={label}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    size={size}
                    value={field.state.value}
                  />
                  <FieldInfo field={field} />
                </>
              );
            }}
          </form.Field>

          <form.Field name="contact.phone">
            {field => {
              const label = {
                children: t('phone', { ns: 'general' }),
                htmlFor: 'contact-phone',
                id: 'label-contact-phone',
              };
              return (
                <>
                  <Input
                    autoComplete="tel"
                    className={createStyles.uiInput}
                    data-testid="input-contact-phone"
                    id="contact-phone"
                    label={label}
                    name={field.name}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    size={size}
                    value={field.state.value}
                  />
                  <FieldInfo field={field} />
                </>
              );
            }}
          </form.Field>
        </Accordion>

        <form.Subscribe
          selector={state => ({
            canSubmit: state.canSubmit,
            name: state.values.name,
            isSubmitting: state.isSubmitting,
            isSubmitted: state.isSubmitted,
          })}
        >
          {({ name, canSubmit, isSubmitting, isSubmitted }) => {
            const hasRequiredValues = (name?.trim?.()?.length ?? 0) > 0;
            return (
              <div className={createStyles.footer}>
                <Button
                  className={clsx((isSubmitting || isSubmitted) && createStyles.noPointer)}
                  data-testid="submit-btn"
                  isDisabled={!hasRequiredValues || !canSubmit || isSubmitting}
                  size="xl"
                  type="submit"
                  visual={isSubmitting || isSubmitted ? 'success' : 'primary'}
                >
                  {isSubmitting || isSubmitted ? (
                    <>
                      <Loader className={createStyles.spinner} /> &nbsp;{t('general:submitting')}
                    </>
                  ) : (
                    t('general:submit')
                  )}
                </Button>
              </div>
            );
          }}
        </form.Subscribe>
      </form>
    </div>
  );
};

export default CreateForm;
